/**
 * Pure scheduling-conflict detection for Game Pass seasons (FE-1693).
 *
 * An admin must not be able to schedule a season whose date range overlaps
 * another season that already occupies a committed time window — i.e. an ACTIVE
 * or SCHEDULED season. Draft, grace, locked, completed and cancelled seasons
 * never block a new schedule (a draft has no committed window yet).
 *
 * The overlap test treats each season as a half-open interval `[start, end)`
 * so two back-to-back seasons (one ends exactly when the next begins) do NOT
 * conflict — that's the intended "auto-start after the previous season ends"
 * shape, not an overlap.
 *
 * Kept framework-free (no React) so it can be unit-tested in isolation and
 * reused by every surface that schedules a season (the Create Season wizard
 * and the Season Details schedule / edit flows).
 */

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { AppStrings } from "../../utils/constants/AppStrings";
import {
  SEASON_API_STATUS,
  type SeasonApiRecord,
  type SeasonApiStatus,
} from "../../types/gamePass";
import type { ScheduleValues } from "../../components/GamePass/CreateSeason/createSeasonData";
import { combineDateAndTime } from "./useUpdateSeason";

dayjs.extend(utc);

/** Lifecycle statuses whose date window blocks a new season's schedule from
 *  overlapping. Only ACTIVE and SCHEDULED seasons hold a committed window for
 *  this guard — draft, grace and locked seasons no longer block a schedule. */
export const SEASON_CONFLICT_STATUSES: ReadonlySet<SeasonApiStatus> = new Set([
  SEASON_API_STATUS.ACTIVE,
  SEASON_API_STATUS.SCHEDULED,
]);

/** A resolved UTC millisecond window for a season's [start, end). */
export type SeasonScheduleRange = {
  startMs: number;
  endMs: number;
};

/** A real Game Pass season runs for days/weeks. Any existing window shorter than
 *  this is treated as an unscheduled placeholder (the Create-Season wizard stamps
 *  a ~1-minute placeholder window on new drafts because the BE requires
 *  end > start on create) and is skipped by the overlap guard so a half-finished
 *  draft can never falsely block another season. */
const MIN_REAL_SEASON_WINDOW_MS = 60 * 60 * 1000; // 1 hour

const toUtcMs = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = dayjs.utc(value);
  return parsed.isValid() ? parsed.valueOf() : null;
};

/**
 * Resolve the candidate season's UTC window from the Schedule step values.
 * Returns `null` only when there is no fixed window to compare — i.e. a
 * date/time field is still missing (or the window is non-positive).
 *
 * Auto-start IS overlap-checked: toggling it on fills a concrete, editable
 * window (start = the active season's end + 1 minute, end = admin-chosen), so
 * the resulting range can still collide with another scheduled / active season.
 * Skipping the check let an auto-started season be scheduled on top of an
 * existing one. The half-open `[start, end)` intersection means the auto-start
 * season never flags the very season it chains after (it begins one minute
 * later), so only genuine overlaps with other seasons are surfaced.
 */
export const resolveCandidateScheduleRange = (
  schedule: ScheduleValues,
): SeasonScheduleRange | null => {
  const startIso = combineDateAndTime(schedule.startDate, schedule.startTime);
  const endIso = combineDateAndTime(schedule.endDate, schedule.endTime);
  const startMs = toUtcMs(startIso);
  const endMs = toUtcMs(endIso);
  if (startMs == null || endMs == null) return null;
  // A non-positive window (end ≤ start) isn't a real schedule to compare —
  // treat it as "no window" rather than risk a degenerate overlap.
  if (startMs >= endMs) return null;
  return { startMs, endMs };
};

/** Half-open interval intersection: `[aStart, aEnd)` overlaps `[bStart, bEnd)`. */
const rangesOverlap = (a: SeasonScheduleRange, b: SeasonScheduleRange): boolean =>
  a.startMs < b.endMs && b.startMs < a.endMs;

export type FindSeasonScheduleConflictArgs = {
  /** The Schedule step values the admin is trying to save. */
  schedule: ScheduleValues;
  /** Every season currently known to the client (any status — this fn filters). */
  existingSeasons: ReadonlyArray<SeasonApiRecord>;
  /** Id of the season being scheduled/edited so it never conflicts with itself. */
  excludeSeasonId?: string | null;
};

/**
 * Returns the first existing season whose window overlaps the candidate
 * schedule, or `null` when there is no conflict (or no fixed window to check).
 * The candidate's own record is skipped via `excludeSeasonId` so editing a
 * season doesn't flag it against itself.
 */
export const findSeasonScheduleConflict = ({
  schedule,
  existingSeasons,
  excludeSeasonId,
}: FindSeasonScheduleConflictArgs): SeasonApiRecord | null => {
  const candidate = resolveCandidateScheduleRange(schedule);
  if (!candidate) return null;

  for (const record of existingSeasons) {
    const recordId = record.id ?? record.season_id ?? null;
    if (excludeSeasonId && recordId === excludeSeasonId) continue;
    if (!SEASON_CONFLICT_STATUSES.has(record.status)) continue;

    const startMs = toUtcMs(record.start_date);
    const endMs = toUtcMs(record.end_date);
    if (startMs == null || endMs == null) continue;
    // Skip seasons with no real window. New drafts are stamped with a tiny
    // placeholder window (the BE requires end > start, so a zero-width one is
    // rejected) until the admin sets real dates on the Schedule step, so an
    // unscheduled draft never blocks another season (FE-1693).
    if (endMs - startMs < MIN_REAL_SEASON_WINDOW_MS) continue;

    if (rangesOverlap(candidate, { startMs, endMs })) {
      return record;
    }
  }
  return null;
};

/**
 * Build the user-facing error message for a detected conflict. Names the
 * conflicting season so the admin knows which one to move; falls back to a
 * generic message when the record has no name.
 */
export const buildSeasonScheduleConflictMessage = (
  conflict?: SeasonApiRecord | null,
): string => {
  const name = conflict?.name?.trim();
  if (!name) return AppStrings.SEASON_SCHEDULE_CONFLICT_GENERIC;
  return AppStrings.SEASON_SCHEDULE_CONFLICT_NAMED.replace(
    "{seasonName}",
    name,
  );
};
